import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Canvas, useThree } from 'react-three-fiber'

import Lerp from './utils/lerp'
import useLerpWorker from './utils/lerp/hook'
import Fa2 from './utils/fa2'
import useFa2Worker from './utils/fa2/hook'
import Labels from './utils/label'

import useZoomfrom, { CameraZoom } from './utils/zoom'
import Container from './utils/container'

import Manager from './entities/manager'
import NeoEdges from './entities/edge/component'
import Nodes from './entities/node/component'

export default function Graph (props) {
  const [zoomRef, zoom] = useZoomfrom(props.defaultCameraPosition, props.defaultCameraZoom)
  const threeRefs = useRef()
  const [nodeCounts, setNodeCounts] = useState([])
  const [edgeCounts, setEdgeCounts] = useState(0)
  const barnesHutThetaRef = useRef(1.6)
  const maxDRef = useRef(50000)
  const lerpFactorRef = useRef(0.3)

  const lerp = useMemo(() => new Lerp(), [])
  const FA2 = useMemo(() => new Fa2(), [])

  const manager = useMemo(() => {
    const manager = new Manager(props.maxNodes, props.maxEdges)
    lerp.setEdgePositionFrom(manager.nodePositions)
    lerp.setEdgePositionTo(manager.nodePositions)
    lerp.setEdgeIndexes([])
    return manager
  }, [lerp, props.maxNodes, props.maxEdges])

  useEffect(() => {
    manager.selectNodes(props.nodes)
    setNodeCounts(manager.counts.nodes)
    lerp.setEdgePositionTo(lerp.from)

    manager.selectEdges(props.edges)
    setEdgeCounts(props.edges.length)
    lerp.setEdgeIndexes(manager.fa2.edgesIndexes)
  }, [lerp, manager, props.nodes, props.edges])

  useEffect(() => {
    barnesHutThetaRef.current = 1.5
    lerpFactorRef.current = 0.3
    maxDRef.current = 50000
  }, [props.nodes.length, props.edges.length])

  useLerpWorker(lerp, manager, props.nodes, props.edges, !props.hideEdges, { lerpFactorRef }, props.runLerp)

  useFa2Worker(
    FA2,
    props.nodes,
    props.edges,
    manager,
    lerp,
    {
      FA2params: props.FA2params,
      lerpFactorRef,
      barnesHutThetaRef,
      maxDRef
    },
    props.rendering
  )

  const nodeBuffers = useMemo(() => {
    return manager.getBuffers()
  }, [manager])

  const edgeBuffers = useMemo(() => {
    return manager.getEdgeBuffers()
  }, [manager])

  return (
    <Container
      manager={manager}
      threeRefs={threeRefs}
      onMouseOver={props.onNodeOver}
      onClick={props.onNodeClick}
      zoom={zoom.k}
    >
      <div style={{ width: '100%', height: '100%' }} ref={zoomRef}>
        <Canvas
          pixelRatio={1}
          camera={{ near: 0.1, far: 3000, position: [0, 0, window.innerHeight * 0.8] }}
        >
          <NeoEdges
            buffers={edgeBuffers}
            count={edgeCounts}
            display={!props.hideEdges}
            zoom={zoom.k}
          />

          {
            nodeCounts.map((d) => {
              return <Nodes key={d.type} buffers={nodeBuffers} shape={d.type} bounds={d.bounds} />
            })
          }

          <CameraZoom zoom={zoom} />
          <CameraRefSetter threeRefs={threeRefs} />
        </Canvas>
      </div>

      <Labels
        labels={props.labels}
        threeRefs={threeRefs}
        manager={manager}
        zoom={zoom.k}
      />
    </Container>
  )
}

function CameraRefSetter (props) {
  const { camera, gl } = useThree()

  useEffect(() => {
    props.threeRefs.current = {
      camera,
      gl
    }
  }, [props.threeRefs, camera, gl])

  return null
}
